export function resolveId(entity) {
    if (!entity) return '';
    return String(entity.id || entity._id || entity.sub || entity);
}

export function toArray(value) {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    return [value];
}

export function extractFirstId(entity, keys = []) {
    if (!entity) return '';
    for (const key of keys) {
        const value = entity[key];
        const id = resolveId(value);
        if (id) return id;
    }
    return '';
}

export function isOwnerOrManager(entry, userId) {
    if (!entry || !userId) return false;
    const ownerId = resolveId(entry.owner);
    const managerId = resolveId(entry.manager);
    const entryId = resolveId(entry);
    return ownerId === userId || managerId === userId || entryId === userId;
}

export function entryHasCourse(entry, courseId) {
    if (!entry || !courseId) return false;
    const normalizedCourseId = String(courseId);
    const directCourseId = extractFirstId(entry, ['courseId', 'course', 'course_id']);
    if (directCourseId === normalizedCourseId) return true;

    const courseCollections = [
        entry.courses,
        entry.assignedCourses,
        entry.products,
        entry.product,
        entry.licence && entry.licence.courses,
        entry.license && entry.license.courses,
    ];
    for (const collection of courseCollections) {
        for (const item of toArray(collection)) {
            const candidateId = resolveId(item && (item.course || item.courseId || item.id || item._id || item));
            if (candidateId === normalizedCourseId) return true;
        }
    }
    return false;
}

export function entryHasAnyCourseReference(entry) {
    if (!entry) return false;
    const directCourseId = extractFirstId(entry, ['courseId', 'course', 'course_id']);
    if (directCourseId) return true;
    const courseCollections = [
        entry.courses,
        entry.assignedCourses,
        entry.products,
        entry.product,
        entry.licence && entry.licence.courses,
        entry.license && entry.license.courses,
    ];
    return courseCollections.some(collection => toArray(collection).length > 0);
}

export function getLicenseIdFromEntry(entry) {
    return resolveId(
        entry?.licenceId || entry?.licenseId || entry?.licence || entry?.license || entry?.id || entry?._id
    );
}

export function getManageableLicenseIds(user, licenseContacts = []) {
    const userId = resolveId(user);
    if (!userId || !Array.isArray(licenseContacts)) return [];
    const ids = licenseContacts
        .filter((entry) => isOwnerOrManager(entry, userId))
        .map(getLicenseIdFromEntry)
        .filter(Boolean);
    return [...new Set(ids)];
}

export function getLicenseIdsFromUser(user) {
    if (!user) return [];
    const ids = [];

    if (Array.isArray(user.assignments)) {
        user.assignments.forEach((assignment) => {
            const licenceId = resolveId(assignment?.licence || assignment?.license || assignment?.licenceId || assignment?.licenseId);
            if (licenceId) ids.push(licenceId);
        });
    }

    if (Array.isArray(user.teamMemberships)) {
        user.teamMemberships.forEach((membership) => {
            const licenceId = resolveId(membership?.licence || membership?.license || membership?.licenceId || membership?.licenseId || membership);
            if (licenceId) ids.push(licenceId);
        });
    }

    return [...new Set(ids)];
}

export function getMesskursInstructorAccessDebug(user, licenseContacts = [], course = null) {
    if (!user || typeof user !== 'object') {
        return { hasAccess: false };
    }

    const userId = resolveId(user);
    const courseId = resolveId(course && (course.id || course.courseId || course));

    const hasOwnLicenses = user.hasTeamLicences === true;
    const canManageLicenses = user.hasFrontendManagedLicences === true;
    const isLicenseManagerRole = user.role === 'licenceManager';

    const hasOwnerOrManagerContact = Array.isArray(licenseContacts)
        && licenseContacts.some(entry => isOwnerOrManager(entry, userId));

    const hasOwnerOrManagerContactForCourse = Array.isArray(licenseContacts)
        && licenseContacts.some(entry => isOwnerOrManager(entry, userId) && entryHasCourse(entry, courseId));

    const hasCourseReferencesInContacts = Array.isArray(licenseContacts)
        && licenseContacts.some(entry => entryHasAnyCourseReference(entry));

    const hasAccess = Boolean(userId) && (
        courseId
            ? (hasOwnerOrManagerContactForCourse || (!hasCourseReferencesInContacts && hasOwnerOrManagerContact))
            : (hasOwnerOrManagerContact || hasOwnLicenses || canManageLicenses || isLicenseManagerRole)
    );

    const contactMatches = Array.isArray(licenseContacts)
        ? licenseContacts
            .map(entry => ({
                entryId: resolveId(entry),
                ownerId: resolveId(entry && entry.owner),
                managerId: resolveId(entry && entry.manager),
                match: isOwnerOrManager(entry, userId),
                matchForCourse: entryHasCourse(entry, courseId),
            }))
            .filter(entry => entry.match)
        : [];

    return {
        hasAccess,
        userId,
        courseId,
        role: user.role || '',
        hasTeamLicences: hasOwnLicenses,
        hasFrontendManagedLicences: canManageLicenses,
        isLicenseManagerRole,
        contactsCount: Array.isArray(licenseContacts) ? licenseContacts.length : 0,
        hasCourseReferencesInContacts,
        hasOwnerOrManagerContact,
        hasOwnerOrManagerContactForCourse,
        contactMatches,
    };
}

export function hasMesskursInstructorAccess(user, licenseContacts = [], course = null) {
    return getMesskursInstructorAccessDebug(user, licenseContacts, course).hasAccess;
}

export function normalizeLicenseContacts(response) {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.results)) return response.results;
    return [];
}
